import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDataMode } from '../../contexts/DataModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  FileCheck, Search, Send, CheckCircle2, XCircle, Clock,
  ChevronLeft, ChevronRight, Eye, TrendingUp, RefreshCw,
  Globe, Building2, X, DollarSign, MessageSquare, Zap, ArrowLeftRight, Loader2, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOCK_PROPOSALS = [
  { id: 'my_pr_001', brief: 'Istanbul Tech Fair 2025', client: 'Global Exhibitions Inc.', amount: 42500, currency: 'TRY', status: 'pending', submitted: '2025-01-08', city: 'Istanbul' },
  { id: 'my_pr_002', brief: 'Arab Health Dubai 2025', client: 'Arab Health Org.', amount: 88000, currency: 'USD', status: 'accepted', submitted: '2024-12-28', city: 'Dubai' },
  { id: 'my_pr_003', brief: 'MWC Barcelona 2025', client: 'Barcelona Tech', amount: 138000, currency: 'EUR', status: 'counter_offered', counter_amount: 125000, counter_currency: 'EUR', counter_message: 'Can you reduce to 125,000?', submitted: '2025-01-06', city: 'Barcelona' },
  { id: 'my_pr_004', brief: 'Hannover Messe 2024', client: 'Bosch GmbH', amount: 95000, currency: 'EUR', status: 'accepted', submitted: '2024-03-12', city: 'Hannover' },
  { id: 'my_pr_005', brief: 'Gulfood Dubai 2025', client: 'Nestlé Middle East', amount: 58000, currency: 'USD', status: 'rejected', submitted: '2024-12-20', city: 'Dubai' },
  { id: 'my_pr_006', brief: 'Automechanika Frankfurt 2025', client: 'BMW Group', amount: 165000, currency: 'EUR', status: 'pending', submitted: '2025-01-07', city: 'Frankfurt' },
];

const STATUS_CONFIG = {
  pending:        { color: 'bg-yellow-100 text-yellow-700',   label: 'Bekliyor',      icon: Clock },
  accepted:       { color: 'bg-emerald-100 text-emerald-700', label: 'Kabul Edildi',  icon: CheckCircle2 },
  rejected:       { color: 'bg-red-100 text-red-600',         label: 'Reddedildi',    icon: XCircle },
  declined:       { color: 'bg-red-100 text-red-600',         label: 'Reddedildi',    icon: XCircle },
  counter_offered:{ color: 'bg-amber-100 text-amber-700',     label: 'Karşı Teklif', icon: ArrowLeftRight },
};

const CURRENCIES = ['EUR', 'USD', 'TRY', 'GBP'];
const PAGE_SIZE = 6;

// ─── Counter Response Modal ──────────────────────────────────────────────────
const CounterResponseModal = ({ proposal, onClose, onResponded, token, isMockMode }) => {
  const [action, setAction] = useState('');
  const [revisedAmount, setRevisedAmount] = useState('');
  const [revisedCurrency, setRevisedCurrency] = useState(proposal.currency || 'EUR');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const handleRespond = async () => {
    if (!action) { setError('Bir işlem seçin'); return; }
    if (action === 'revise' && (!revisedAmount || isNaN(Number(revisedAmount)) || Number(revisedAmount) <= 0)) {
      setError('Geçerli bir tutar girin');
      return;
    }
    setSending(true); setError(null);
    const body = { action, message };
    if (action === 'revise') { body.revised_amount = Number(revisedAmount); body.revised_currency = revisedCurrency; }
    try {
      if (!isMockMode) {
        await axios.post(`${API}/contractor/proposals/${proposal.id}/counter-response`, body, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await new Promise(r => setTimeout(r, 700));
      }
      if (action === 'accept') toast.success('Karşı teklif kabul edildi!');
      else if (action === 'decline') toast('Karşı teklif reddedildi', { icon: '⚠️' });
      else toast.success('Revize teklif gönderildi!');
      onResponded();
    } catch (e) {
      setError(e?.response?.data?.detail || 'İşlem başarısız');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="counter-response-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-amber-100 bg-amber-50">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-amber-600" />
            <h3 className="font-bold text-gray-900">Karşı Teklife Yanıt Ver</h3>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-400 hover:text-gray-600" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Counter info */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs text-amber-600 mb-1">Müşterinin Karşı Teklifi</p>
            <p className="text-xl font-black text-amber-800">{proposal.counter_amount?.toLocaleString()} <span className="text-sm font-semibold">{proposal.counter_currency}</span></p>
            {proposal.counter_message && <p className="text-xs text-amber-600 mt-2 italic">"{proposal.counter_message}"</p>}
          </div>

          {/* Action choice */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-600">Yanıtınız</label>
            {[
              { key: 'accept', label: 'Kabul Et', desc: `${proposal.counter_amount?.toLocaleString()} ${proposal.counter_currency} ile devam et`, color: 'border-emerald-300 text-emerald-700 bg-emerald-50' },
              { key: 'decline', label: 'Reddet', desc: 'Teklifinizi geri çekin', color: 'border-red-300 text-red-600 bg-red-50' },
              { key: 'revise', label: 'Revize Teklif', desc: 'Yeni bir tutar öner', color: 'border-amber-300 text-amber-700 bg-amber-50' },
            ].map(opt => (
              <button key={opt.key} onClick={() => setAction(opt.key)} data-testid={`counter-response-${opt.key}`}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${action === opt.key ? opt.color + ' ring-2 ring-offset-1 ring-current' : 'border-gray-200 hover:border-gray-300'}`}>
                <p className="font-semibold text-sm">{opt.label}</p>
                <p className="text-xs opacity-70 mt-0.5">{opt.desc}</p>
              </button>
            ))}
          </div>

          {/* Revised amount fields */}
          {action === 'revise' && (
            <div className="space-y-3 border-t border-gray-100 pt-3">
              <div className="flex gap-2">
                <input type="number" value={revisedAmount} onChange={e => setRevisedAmount(e.target.value)}
                  placeholder="Yeni tutar" data-testid="revised-amount-input"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                <select value={revisedCurrency} onChange={e => setRevisedCurrency(e.target.value)}
                  className="w-24 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 appearance-none bg-white">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2}
                placeholder="Revize nedeninizi kısaca açıklayın..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          )}

          {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" />{error}</p>}

          <div className="flex gap-2 pt-1">
            <Button onClick={handleRespond} disabled={sending || !action} data-testid="submit-counter-response-btn"
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {sending ? 'Gönderiliyor...' : 'Onayla'}
            </Button>
            <Button variant="outline" onClick={onClose} className="px-4">İptal</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContractorMyProposals = () => {
  const { isMockMode } = useDataMode();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const handleMessage = async (p) => {
    const contractorOrgId = user?.org_memberships?.[0]?.org_id || '';
    const exhibitorOrgId = p.exhibitor_org_id || '';
    if (!contractorOrgId || !exhibitorOrgId) { navigate('/app/contractor/messages'); return; }
    try {
      const res = await axios.post(
        `${API}/messages/conversations`,
        { brief_id: p.brief_id || '', proposal_id: p.id, exhibitor_org_id: exhibitorOrgId, contractor_org_id: contractorOrgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/app/contractor/messages?conv=${res.data.conversation_id}`);
    } catch { navigate('/app/contractor/messages'); }
  };
  const [proposals, setProposals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [counterTarget, setCounterTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isMockMode) {
        await new Promise(r => setTimeout(r, 500));
        setProposals(MOCK_PROPOSALS);
        setTotal(MOCK_PROPOSALS.length);
      } else {
        const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
        if (statusFilter !== 'all') params.status = statusFilter;
        const res = await axios.get(`${API}/contractor/proposals`, {
          headers: { Authorization: `Bearer ${token}` }, params,
        });
        // Normalize API response
        const items = (res.data.items || []).map(p => ({
          id: p.id,
          brief_id: p.brief_id,
          exhibitor_org_id: p.exhibitor_org_id,
          brief: p.brief_event?.show || p.brief_id,
          client: p.exhibitor_org_name || p.exhibitor_org_id,
          city: p.brief_event?.city || '—',
          amount: p.amount,
          currency: p.currency,
          status: p.status === 'declined' ? 'rejected' : p.status,
          submitted: p.created_at?.split('T')[0],
          delivery_days: p.delivery_days,
          notes: p.notes,
          sqm: p.brief_stand ? `${p.brief_stand.w_m}×${p.brief_stand.d_m}m` : '—',
          budget: p.brief_budget ? `${p.brief_budget.min?.toLocaleString()}–${p.brief_budget.max?.toLocaleString()} ${p.brief_budget.currency}` : '—',
          is_first_response: p.is_first_response || false,
          counter_amount: p.counter_amount,
          counter_currency: p.counter_currency,
          counter_message: p.counter_message,
        }));
        setProposals(items);
        setTotal(res.data.total || 0);
      }
    } catch {
      setProposals(MOCK_PROPOSALS);
      setTotal(MOCK_PROPOSALS.length);
    } finally { setLoading(false); }
  }, [isMockMode, token, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = proposals.filter(p => {
    const q = search.toLowerCase();
    return (!q || (p.brief || '').toLowerCase().includes(q) || (p.client || '').toLowerCase().includes(q) || (p.city || '').toLowerCase().includes(q)) &&
           (statusFilter === 'all' || p.status === statusFilter);
  });

  const totalPages = isMockMode ? Math.ceil(filtered.length / PAGE_SIZE) : Math.ceil(total / PAGE_SIZE);
  const paged = isMockMode ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : filtered;
  const stats = {
    total: isMockMode ? proposals.length : total,
    accepted: proposals.filter(p => p.status === 'accepted').length,
    winRate: proposals.length ? Math.round((proposals.filter(p => p.status === 'accepted').length / proposals.length) * 100) : 0,
  };

  return (
    <div className="space-y-6">
      {/* Counter response modal */}
      {counterTarget && (
        <CounterResponseModal
          proposal={counterTarget}
          token={token}
          isMockMode={isMockMode}
          onClose={() => setCounterTarget(null)}
          onResponded={async () => { setCounterTarget(null); setSelected(null); await load(); }}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Proposals</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track the status of your submitted quotes</p>
        </div>
        <div className="flex items-center gap-2">
          {!isMockMode && <Button variant="outline" size="sm" onClick={load} className="gap-1.5 h-8"><RefreshCw className="h-3.5 w-3.5" />Refresh</Button>}
          <Button className="bg-green-600 hover:bg-green-700 text-white gap-2 h-9 text-sm" onClick={() => window.location.href='/app/contractor/brief-board'}>
            <Send className="h-4 w-4"/>Find Briefs
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Proposals', value: stats.total, color: 'text-blue-600' },
          { label: 'Accepted', value: stats.accepted, color: 'text-emerald-600' },
          { label: 'Win Rate', value: `${stats.winRate}%`, color: 'text-green-600' },
        ].map((s,i)=>(
          <Card key={i} className="border-0 shadow-sm">
            <div className="p-4 flex items-center gap-3">
              <span className={`text-2xl font-bold ${s.color}`}>{s.value}</span>
              <span className="text-sm text-gray-500">{s.label}</span>
            </div>
          </Card>
        ))}
      </div>

      <Card className="border-0 shadow-sm">
        <div className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"/>
            <Input placeholder="Search brief or client..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} className="pl-9 bg-gray-50 border-gray-200"/>
          </div>
          <select value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(1);}}
            className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500">
            <option value="all">All Statuses</option>
            <option value="pending">Bekliyor</option>
            <option value="counter_offered">Karşı Teklif</option>
            <option value="accepted">Kabul Edildi</option>
            <option value="rejected">Reddedildi</option>
          </select>
          <span className="text-sm text-gray-400 ml-auto">{filtered.length} proposals</span>
        </div>
      </Card>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_,i)=><div key={i} className="h-20 bg-gray-100 animate-pulse rounded-xl"/>)}</div>
      ) : paged.length === 0 ? (
        <div className="text-center py-16 text-gray-400"><FileCheck className="h-12 w-12 mx-auto mb-3 text-gray-300"/><p>No proposals found</p></div>
      ) : (
        <Card className="border-0 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wide">Brief</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wide">Client</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wide">Amount</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wide">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wide">Date</th>
                  <th/>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paged.map(p => {
                  const sc = STATUS_CONFIG[p.status];
                  const Icon = sc.icon;
                  return (
                    <tr key={p.id} className={`hover:bg-gray-50 transition-colors ${p.status === 'counter_offered' ? 'bg-amber-50/50' : ''}`}>
                      <td className="py-3 px-4">
                        <p className="font-semibold text-gray-900 text-xs">{p.brief}</p>
                        <p className="text-xs text-gray-400">{p.city}</p>
                        {p.status === 'counter_offered' && p.counter_amount && (
                          <span className="text-[10px] text-amber-700 flex items-center gap-0.5 mt-0.5">
                            <ArrowLeftRight className="h-2.5 w-2.5" />{p.counter_amount?.toLocaleString()} {p.counter_currency}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-700 text-xs">{p.client}</td>
                      <td className="py-3 px-4 font-mono text-xs font-semibold text-gray-900">{p.amount?.toLocaleString()} {p.currency}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${sc.color}`}><Icon className="h-3 w-3"/>{sc.label}</span>
                          {p.is_first_response && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200" title="You were the first to submit on this brief!">
                              <Zap className="h-2.5 w-2.5" />1st
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-400">{new Date(p.submitted).toLocaleDateString('en-GB')}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={()=>setSelected(p)} data-testid={`view-proposal-${p.id}`}><Eye className="h-3.5 w-3.5"/></Button>
                          {p.status === 'counter_offered' && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-600 hover:bg-amber-50"
                              data-testid={`respond-counter-${p.id}`}
                              title="Karşı teklife yanıt ver"
                              onClick={() => setCounterTarget(p)}>
                              <ArrowLeftRight className="h-3.5 w-3.5"/>
                            </Button>
                          )}
                          {p.status === 'accepted' && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-green-600 hover:bg-green-50"
                              title="View in Projects"
                              onClick={() => navigate('/app/contractor/projects')}>
                              <TrendingUp className="h-3.5 w-3.5"/>
                            </Button>
                          )}
                          {(p.status === 'accepted' || p.status === 'pending') && !isMockMode && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-blue-600 hover:bg-blue-50"
                              data-testid={`msg-btn-${p.id}`}
                              onClick={() => handleMessage(p)}>
                              <MessageSquare className="h-3.5 w-3.5"/>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">{(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE,filtered.length)} / {filtered.length}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-7 px-2" disabled={page===1} onClick={()=>setPage(p=>p-1)}><ChevronLeft className="h-4 w-4"/></Button>
                {[...Array(totalPages)].map((_,i)=><Button key={i} variant={page===i+1?'default':'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={()=>setPage(i+1)}>{i+1}</Button>)}
                <Button variant="outline" size="sm" className="h-7 px-2" disabled={page===totalPages} onClick={()=>setPage(p=>p+1)}><ChevronRight className="h-4 w-4"/></Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="my-proposal-detail-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Teklif Detayı</h3>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2 text-sm">
              {[
                ['Brief', selected.brief],
                ['Şehir', selected.city],
                ['Stand Boyutu', selected.sqm || '—'],
                ['Müşteri Bütçesi', selected.budget || '—'],
                ['Teklifiniz', `${selected.amount?.toLocaleString()} ${selected.currency}`],
                ['Teslimat', selected.delivery_days ? `${selected.delivery_days} gün` : '—'],
                ['Durum', STATUS_CONFIG[selected.status]?.label],
                ['Gönderildi', selected.submitted ? new Date(selected.submitted).toLocaleDateString('tr-TR') : '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500">{k}</span><span className="font-medium text-right max-w-[60%]">{v}</span>
                </div>
              ))}
              {selected.notes && (
                <div className="pt-2">
                  <p className="text-xs text-gray-400 mb-1">Notunuz</p>
                  <p className="text-sm bg-gray-50 rounded-lg p-3 text-gray-600 italic">"{selected.notes}"</p>
                </div>
              )}
              {/* Counter offer section */}
              {selected.status === 'counter_offered' && selected.counter_amount && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-2">
                  <div className="flex items-center gap-1.5 mb-2">
                    <ArrowLeftRight className="h-4 w-4 text-amber-600" />
                    <p className="text-sm font-bold text-amber-800">Müşterinin Karşı Teklifi</p>
                  </div>
                  <p className="text-xl font-black text-amber-700">{selected.counter_amount?.toLocaleString()} {selected.counter_currency}</p>
                  {selected.counter_message && <p className="text-xs text-amber-600 mt-2 italic">"{selected.counter_message}"</p>}
                  <Button onClick={() => { setCounterTarget(selected); setSelected(null); }}
                    data-testid="respond-to-counter-btn"
                    className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-white gap-2">
                    <ArrowLeftRight className="h-4 w-4" />Karşı Teklife Yanıt Ver
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorMyProposals;
